// SPDX-License-Identifier: LicenseRef-MMS-WIRE
// Copyright (c) 2016-2026 Metal Management Solutions (Pty) Ltd

import { ApplicationConfig, importProvidersFrom, inject, provideZoneChangeDetection } from '@angular/core';
import { tap, finalize } from 'rxjs/operators';
import { MAT_FORM_FIELD_DEFAULT_OPTIONS } from "@angular/material/form-field";
import { routes } from "./sub-modules/app-routing.module";
import { provideAnimations } from "@angular/platform-browser/animations";
import { MAT_TOOLTIP_DEFAULT_OPTIONS, MatTooltipDefaultOptions } from "@angular/material/tooltip";
import { FullscreenOverlayContainer, OverlayContainer } from "@angular/cdk/overlay";
import { ErrorStateMatcher, ShowOnDirtyErrorStateMatcher } from "@angular/material/core";
import { HTTP_INTERCEPTORS, HttpInterceptorFn, provideHttpClient, withInterceptors, withInterceptorsFromDi } from "@angular/common/http";
import { includeBearerTokenInterceptor } from "keycloak-angular";
import { ActiveAccountInterceptor } from "./interceptor/active-account.interceptor";
import { provideRouter } from "@angular/router";
import { SecurePipe } from "./shared/pipes";
import { MatomoModule, MatomoRouterModule } from "ngx-matomo-client";
import { SocketIoConfig, SocketIoModule } from "ngx-socket-io";
import { LoadingBarModule, LoadingBarService } from "@ngx-loading-bar/core";
import { NGX_LOADING_BAR_IGNORED } from "@ngx-loading-bar/http-client";
import { provideChunkReload, reloadOnceOnChunkError } from "./error-handlers/chunk-reload";

const WireOptions: MatTooltipDefaultOptions = {
    showDelay: 0,
    hideDelay: 0,
    touchGestures: 'auto',
    position: 'below',
    touchendHideDelay: 0,
    disableTooltipInteractivity: true,
};

const analytic_domain = 'https://analytics.example.com/matomo.js';
const analytic_sites = {
    'http://localhost:4200': 1,
    'https://production.url': 2
};

const matomoModule = MatomoModule.forRoot({
    scriptUrl: analytic_domain,
    trackers: [
        {
            trackerUrl: window.location.origin,
            siteId: analytic_sites[window.location.origin] || 0
        }
    ]
});

const socketIoConfig: SocketIoConfig = {
    url: window.location.origin,
    options: {
        timeout: 20000,
        reconnection: true,
        reconnectionDelay: 1000,
        randomizationFactor: 0.5,
        reconnectionDelayMax: 10000,
        transports: ['websocket', 'polling']
    }
};

/**
 * Functional interceptor replacement for LoadingBarHttpClientModule.
 * LoadingBarHttpClientModule can't be used with provideHttpClient() because it
 * internally imports HttpClientModule, which conflicts with provideHttpClient().
 */
const loadingBarInterceptor: HttpInterceptorFn = (req, next) => {
    if (req.context.get(NGX_LOADING_BAR_IGNORED)) {
        return next(req);
    }
    const loader = inject(LoadingBarService);
    const ref = loader.useRef('http');
    let started = false;
    return next(req).pipe(
        tap(() => {
            if (!started) {
                ref.start();
                started = true;
            }
        }),
        finalize(() => started && ref.complete())
    );
};

export const appConfig: ApplicationConfig = {
    providers: [
        provideRouter(routes, reloadOnceOnChunkError),
        provideChunkReload(),
        provideAnimations(),
        importProvidersFrom(matomoModule),
        importProvidersFrom(MatomoRouterModule),
        importProvidersFrom(SocketIoModule.forRoot(socketIoConfig)),
        importProvidersFrom(LoadingBarModule),
        { provide: MAT_TOOLTIP_DEFAULT_OPTIONS, useValue: WireOptions },
        { provide: 'Window', useValue: window },
        [{ provide: OverlayContainer, useClass: FullscreenOverlayContainer }],
        provideZoneChangeDetection({ eventCoalescing: true }),
        { provide: ErrorStateMatcher, useClass: ShowOnDirtyErrorStateMatcher },
        { provide: MAT_FORM_FIELD_DEFAULT_OPTIONS, useValue: { subscriptSizing: 'dynamic' } },
        provideHttpClient(withInterceptors([includeBearerTokenInterceptor, loadingBarInterceptor]), withInterceptorsFromDi()),
        { provide: HTTP_INTERCEPTORS, useClass: ActiveAccountInterceptor, multi: true },
        { provide: SecurePipe, useClass: SecurePipe }
    ]
};
